import { AxiosError } from 'axios';
import api from '../api/apiClient';
import { PaymentMethod, AddPaymentMethodRequest } from '../types/payment';

/**
 * Fetches all stored payment methods (cards or bank accounts).
 * GET /payments/methods
 */
export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  try {
    const response = await api.getWithParams<{ data: PaymentMethod[] }>('/payments/methods');
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching payment methods:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Adds a new payment method from a Stripe SetupIntent (never raw card data).
 * POST /payments/methods
 */
export async function addPaymentMethod(
  request: AddPaymentMethodRequest
): Promise<PaymentMethod> {
  try {
    const response = await api.postWithData<{ data: PaymentMethod }>('/payments/methods', request);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error adding payment method:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Creates a SetupIntent for saving a card on file (wallet), not tied to a booking.
 * POST /payments/methods/setup-intent
 */
export async function createSetupIntentForSavedMethod(): Promise<{
  clientSecret: string;
  setupIntentId: string;
}> {
  const response = await api.postWithData<{
    clientSecret: string;
    setupIntentId: string;
  }>('/payments/methods/setup-intent', {});
  return response.data;
}

/**
 * Deletes a payment method by its ID.
 * DELETE /payments/methods/:id
 */
export async function deletePaymentMethod(id: string): Promise<void> {
  try {
    await api.deleteWithParams<void>(`/payments/methods/${id}`);
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error(`Error deleting payment method ${id}:`, err.response?.data || err.message);
    throw err;
  }
}

/**
 * Sets a payment method as default.
 * PUT /payments/methods/:id/default
 */
export async function setDefaultPaymentMethod(id: string): Promise<PaymentMethod> {
  try {
    const response = await api.putWithData<{ data: PaymentMethod }>(`/payments/methods/${id}/default`, {});
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error(`Error setting default payment method ${id}:`, err.response?.data || err.message);
    throw err;
  }
}

/**
 * Creates a Setup Intent for collecting payment method without charging.
 * POST /payments/create-setup-intent-for-booking
 * 
 * This is the NEW payment flow:
 * 1. Collect payment method at booking (Setup Intent - no charge)
 * 2. Stylist confirms -> charge the stylist's configured down payment (defaults to off / $0)
 * 3. Service completes -> charge the remaining balance
 * 
 * @param appointmentId The ID of the appointment to create a setup intent for
 * @returns Object containing the client secret and setup intent ID
 */
/**
 * Creates a Stripe SetupIntent for the card-first booking flow.
 * No appointment is created yet — the appointment is created after the card is confirmed.
 * POST /payments/pre-booking-setup-intent
 */
export async function createPreBookingSetupIntent(
  stylistId: string
): Promise<{ clientSecret: string; setupIntentId: string; downPaymentPolicy?: { enabled: boolean; percentage: number } }> {
  try {
    const response = await api.postWithData<{
      clientSecret: string;
      setupIntentId: string;
      downPaymentPolicy?: { enabled: boolean; percentage: number };
    }>('/payments/pre-booking-setup-intent', { stylistId });
    return response.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error creating pre-booking setup intent:', err.response?.data || err.message);
    throw err;
  }
}

export async function createSetupIntentForBooking(
  appointmentId: string
): Promise<{ clientSecret: string; setupIntentId: string }> {
  try {
    const response = await api.postWithData<{ clientSecret: string; setupIntentId: string }>(
      '/payments/create-setup-intent',
      { appointmentId }
    );
    return response.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error creating setup intent:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Confirms the Setup Intent and stores the payment method on the appointment.
 * POST /payments/confirm-setup-intent
 *
 * After the user completes the Setup Intent, the backend retrieves the payment
 * method directly from Stripe using the setupIntentId — so paymentMethodId is
 * not sent in the request body. The parameter is kept in the signature for
 * call-site clarity (it comes from stripe.confirmCardSetup) but is intentionally
 * not forwarded.
 *
 * @param appointmentId The ID of the appointment
 * @param _paymentMethodId Derived locally from Stripe; not forwarded — backend retrieves it via setupIntentId
 * @param setupIntentId The Setup Intent ID
 */
export async function confirmSetupIntentAndStorePaymentMethod(
  appointmentId: string,
  _paymentMethodId: string,
  setupIntentId: string
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await api.postWithData<{ success: boolean; message: string }>(
      '/payments/confirm-setup-intent',
      { appointmentId, setupIntentId }
    );
    return response.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error confirming setup intent:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Creates a payment intent for an appointment (down payment by default).
 * POST /payments/create-payment-intent
 * 
 * NOTE: This is the OLD flow - kept for backward compatibility.
 * NEW bookings should use createSetupIntentForBooking() instead.
 * 
 * @param appointmentId The ID of the appointment to create a payment intent for
 * @returns Object containing the client secret and optionally amount information
 */
export async function createPaymentIntent(
  appointmentId: string
): Promise<{
  clientSecret?: string | null;
  amount?: number;
  noPaymentNeeded?: boolean;
  deferredCharge?: boolean;
}> {
  try {
    const response = await api.postWithData<{
      clientSecret?: string | null;
      amount?: number;
      noPaymentNeeded?: boolean;
      deferredCharge?: boolean;
    }>(
      '/payments/create-payment-intent',
      { appointmentId }
    );
    return response.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error creating payment intent:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Creates a final payment intent (remaining balance).
 * POST /payments/create-final-payment-intent
 */
export async function createFinalPaymentIntent(
  appointmentId: string
): Promise<{ clientSecret: string }> {
  try {
    const response = await api.postWithData<{ clientSecret: string }>('/payments/create-final-payment-intent', { appointmentId });
    return response.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error creating final payment intent:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Confirms the final payment for an appointment.
 * POST /payments/confirm-final-payment
 */
export async function confirmFinalPayment(
  appointmentId: string
): Promise<void> {
  try {
    await api.postWithData<void>('/payments/confirm-final-payment', { appointmentId });
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error confirming final payment:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Issues a refund for a given appointment.
 * POST /payments/refund
 */
export async function refundPayment(
  appointmentId: string,
  reason: string
): Promise<void> {
  try {
    await api.postWithData<void>('/payments/refund', { appointmentId, reason });
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error refunding payment:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Retrieves business revenue statistics.
 * GET /payments/business-revenue
 */
export async function getBusinessRevenue(): Promise<any> {
  try {
    const response = await api.getWithParams<{ data: any }>('/payments/business-revenue');
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching business revenue:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Get stylist's down payment policy (reflects settings set by stylist e.g. on mobile).
 * GET /profile/stylists/:stylistId/down-payment-policy
 */
export async function getStylistDownPaymentPolicy(stylistId: string): Promise<{
  enabled: boolean;
  percentage: number;
}> {
  if (!stylistId || typeof stylistId !== 'string' || !stylistId.trim()) {
    return { enabled: false, percentage: 0 };
  }
  try {
    const response = await api.get(`/profile/stylists/${stylistId.trim()}/down-payment-policy`);
    const policy = (response.data as { data?: { enabled?: boolean; percentage?: number } })?.data;
    const result = {
      enabled: policy?.enabled === true,
      percentage: policy?.percentage ?? 0
    };
    return result;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      const err = error as AxiosError<any>;
      console.error('Error fetching down payment policy:', err.response?.data || err.message);
    }
    return { enabled: false, percentage: 0 };
  }
}

/**
 * Returns an icon name for a payment method.
 */
export function getPaymentMethodIcon(method: PaymentMethod): string {
  if (method.type === 'card') {
    return 'card-outline';
  }
  return 'cash-outline';
}

/**
 * Formats a payment method for display (brand and last 4 digits).
 */
export function formatPaymentMethodDisplay(method: PaymentMethod): string {
  if (method.type === 'card' && method.card) {
    return `${method.card.brand} ending in ${method.card.last4}`;
  }
  if (method.type === 'bank_account' && method.bankAccount) {
    return `${method.bankAccount.bankName} ending in ${method.bankAccount.last4}`;
  }
  return 'Unknown Payment Method';
}

const paymentService = {
  getPaymentMethods,
  addPaymentMethod,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  createPreBookingSetupIntent,
  createSetupIntentForBooking,
  createSetupIntentForSavedMethod,
  confirmSetupIntentAndStorePaymentMethod,
  createPaymentIntent,
  createFinalPaymentIntent,
  confirmFinalPayment,
  refundPayment,
  getBusinessRevenue,
  getStylistDownPaymentPolicy,
  getPaymentMethodIcon,
  formatPaymentMethodDisplay,
};

export default paymentService;